/**
 * IMAGO Luxury — Premium Product Image Zoom
 * Apple × Hermès level interaction quality
 * GPU-accelerated, 60fps, zero-dependency
 */

(function () {
  'use strict';

  // ─── CONFIG ────────────────────────────────────────────────────────────────
  const CONFIG = {
    zoomScale: 3.0,
    easing: 0.08,          // interpolation factor (lower = smoother/slower)
    enterDuration: 280,    // ms
    exitDuration: 220,     // ms
    imageWidth: 3000,      // Shopify CDN max width
  };

  // ─── STATE ─────────────────────────────────────────────────────────────────
  let rafId = null;
  let currentGallery = null;

  // ─── CURSOR ────────────────────────────────────────────────────────────────
  function createCursor() {
    const el = document.createElement('div');
    el.className = 'lz-cursor';
    el.innerHTML = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1"/>
      <line x1="12" y1="7" x2="12" y2="17" stroke="currentColor" stroke-width="1"/>
      <line x1="7" y1="12" x2="17" y2="12" stroke="currentColor" stroke-width="1"/>
    </svg>`;
    document.body.appendChild(el);
    return el;
  }

  // ─── UPGRADE IMAGE SRC ─────────────────────────────────────────────────────
  function getHighResSrc(img) {
    const src = img.src || img.currentSrc || '';
    // Replace Shopify CDN size param with 3000px
    return src.replace(/(_\d+x\d*|_\d*x\d+)(\.[a-z]+)(\?|$)/i, `_${CONFIG.imageWidth}x$2$3`)
              .replace(/width=\d+/, `width=${CONFIG.imageWidth}`);
  }

  // ─── ZOOM INSTANCE ─────────────────────────────────────────────────────────
  function LuxuryZoom(wrapper) {
    const img = wrapper.querySelector('img');
    if (!img) return;

    let active = false;
    let mouseX = 0, mouseY = 0;
    let currentX = 0, currentY = 0;
    let targetX = 0, targetY = 0;
    let cursor = null;

    // Preload high-res
    const hiRes = new Image();
    hiRes.src = getHighResSrc(img);

    // Setup wrapper
    wrapper.style.cssText += `
      overflow: hidden;
      cursor: none;
      position: relative;
    `;

    // Setup img
    img.style.cssText += `
      will-change: transform;
      transform-origin: 0 0;
      transition: transform ${CONFIG.enterDuration}ms cubic-bezier(.22,.61,.36,1),
                  opacity  ${CONFIG.enterDuration}ms cubic-bezier(.22,.61,.36,1);
      display: block;
      width: 100%;
      height: 100%;
      object-fit: cover;
    `;

    cursor = createCursor();

    function onEnter(e) {
      active = true;
      // Switch to high-res src
      if (hiRes.complete) img.src = hiRes.src;

      const rect = wrapper.getBoundingClientRect();
      mouseX = e.clientX - rect.left;
      mouseY = e.clientY - rect.top;
      currentX = mouseX;
      currentY = mouseY;

      img.style.transform = `scale3d(${CONFIG.zoomScale}, ${CONFIG.zoomScale}, 1) translate3d(0,0,0)`;
      cursor.classList.add('lz-cursor--active');
      loop();
    }

    function onLeave() {
      active = false;
      img.style.transition = `transform ${CONFIG.exitDuration}ms cubic-bezier(.22,.61,.36,1)`;
      img.style.transform = `scale3d(1,1,1) translate3d(0,0,0)`;
      cursor.classList.remove('lz-cursor--active');
      cursor.style.opacity = '0';
      cancelAnimationFrame(rafId);
    }

    function onMove(e) {
      const rect = wrapper.getBoundingClientRect();
      mouseX = e.clientX - rect.left;
      mouseY = e.clientY - rect.top;

      // Move cursor
      cursor.style.transform = `translate3d(${e.clientX}px, ${e.clientY}px, 0)`;
      cursor.style.opacity = '1';
    }

    function loop() {
      if (!active) return;

      // Lerp
      currentX += (mouseX - currentX) * CONFIG.easing;
      currentY += (mouseY - currentY) * CONFIG.easing;

      const rect = wrapper.getBoundingClientRect();
      const w = rect.width;
      const h = rect.height;

      // Calculate translate so zoom follows cursor
      const maxX = w * (CONFIG.zoomScale - 1);
      const maxY = h * (CONFIG.zoomScale - 1);
      targetX = -(currentX / w) * maxX;
      targetY = -(currentY / h) * maxY;

      img.style.transition = 'none';
      img.style.transform = `scale3d(${CONFIG.zoomScale},${CONFIG.zoomScale},1) translate3d(${targetX / CONFIG.zoomScale}px,${targetY / CONFIG.zoomScale}px,0)`;

      rafId = requestAnimationFrame(loop);
    }

    // ── Touch (mobile) ──────────────────────────────────────────────────────
    let touchStartDist = 0;
    let touchScale = 1;
    let touchOffsetX = 0, touchOffsetY = 0;
    let lastTouchX = 0, lastTouchY = 0;
    let touchActive = false;
    let lastTap = 0;

    function getTouchDist(t) {
      const dx = t[0].clientX - t[1].clientX;
      const dy = t[0].clientY - t[1].clientY;
      return Math.sqrt(dx * dx + dy * dy);
    }

    wrapper.addEventListener('touchstart', (e) => {
      if (e.touches.length === 2) {
        touchStartDist = getTouchDist(e.touches);
        e.preventDefault();
      } else if (e.touches.length === 1) {
        const now = Date.now();
        if (now - lastTap < 300) {
          // Double tap
          touchActive = !touchActive;
          touchScale = touchActive ? CONFIG.zoomScale : 1;
          touchOffsetX = 0; touchOffsetY = 0;
          applyTouch();
          e.preventDefault();
        }
        lastTap = now;
        lastTouchX = e.touches[0].clientX;
        lastTouchY = e.touches[0].clientY;
      }
    }, { passive: false });

    wrapper.addEventListener('touchmove', (e) => {
      if (e.touches.length === 2) {
        const dist = getTouchDist(e.touches);
        touchScale = Math.min(Math.max((dist / touchStartDist) * touchScale, 1), 4);
        applyTouch();
        e.preventDefault();
      } else if (e.touches.length === 1 && touchScale > 1) {
        const dx = e.touches[0].clientX - lastTouchX;
        const dy = e.touches[0].clientY - lastTouchY;
        touchOffsetX += dx;
        touchOffsetY += dy;
        lastTouchX = e.touches[0].clientX;
        lastTouchY = e.touches[0].clientY;
        applyTouch();
        e.preventDefault();
      }
    }, { passive: false });

    wrapper.addEventListener('touchend', () => {
      if (touchScale <= 1) {
        touchActive = false;
        touchScale = 1;
        touchOffsetX = 0;
        touchOffsetY = 0;
        applyTouch();
      }
    });

    function applyTouch() {
      img.style.transition = `transform 200ms cubic-bezier(.22,.61,.36,1)`;
      img.style.transform = `scale3d(${touchScale},${touchScale},1) translate3d(${touchOffsetX / touchScale}px,${touchOffsetY / touchScale}px,0)`;
    }

    // ── Reduced motion ──────────────────────────────────────────────────────
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!prefersReduced) {
      wrapper.addEventListener('mouseenter', onEnter);
      wrapper.addEventListener('mouseleave', onLeave);
      wrapper.addEventListener('mousemove', onMove);
    }

    // ── Accessibility ───────────────────────────────────────────────────────
    wrapper.setAttribute('role', 'img');
    wrapper.setAttribute('aria-label', img.alt || 'Product image — hover to zoom');
    wrapper.setAttribute('tabindex', '0');
  }

  // ─── INIT ──────────────────────────────────────────────────────────────────
  function init() {
    // Target Dawn's media gallery wrappers
    const selectors = [
      '.product__media-item .media',
      '.product-media-container .media',
      '.product__media .media',
      '[data-media-type="image"] .media',
    ];

    selectors.forEach(sel => {
      document.querySelectorAll(sel).forEach(wrapper => {
        if (!wrapper.dataset.lzInit) {
          wrapper.dataset.lzInit = '1';
          new LuxuryZoom(wrapper);
        }
      });
    });
  }

  // ─── OBSERVE for variant/gallery changes ───────────────────────────────────
  const observer = new MutationObserver(() => init());
  observer.observe(document.body, { childList: true, subtree: true });

  // ─── CSS ───────────────────────────────────────────────────────────────────
  const style = document.createElement('style');
  style.textContent = `
    .lz-cursor {
      position: fixed;
      top: 0; left: 0;
      width: 40px; height: 40px;
      margin: -20px 0 0 -20px;
      pointer-events: none;
      z-index: 99999;
      color: #fff;
      mix-blend-mode: difference;
      opacity: 0;
      transition: opacity 200ms cubic-bezier(.22,.61,.36,1),
                  transform 0ms linear;
      will-change: transform, opacity;
    }
    .lz-cursor--active {
      opacity: 1 !important;
    }
    @media (prefers-reduced-motion: reduce) {
      .lz-cursor { display: none; }
    }
  `;
  document.head.appendChild(style);

  // ─── BOOT ──────────────────────────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
